<script setup lang="ts">
import { useDeveloperRepAccountQuery } from '~/api/queries/useDeveloperRepAccount';

const props = withDefaults(defineProps<{ variant?: 'menu' | 'sheet' | 'drawer'; verifiedOnly?: boolean }>(), {
  variant: 'menu',
  verifiedOnly: false,
});
const emit = defineEmits<{ (e: 'navigate'): void }>();

const PROFILE_ICONS = { developer: '🏗️', personal: '👤' } as const;

const { t } = useI18n();
const route = useRoute();
const { data: me } = useDeveloperRepAccountQuery();

const affiliation = computed(() => me.value?.affiliation ?? null);
const latestStatus = computed(() => me.value?.latest?.status ?? null);
const inWorkspace = computed(
  () =>
    route.path === '/developer' || (route.path.startsWith('/developer/') && !route.path.startsWith('/developer/apply')),
);
const visible = computed(() => !!me.value && (!props.verifiedOnly || !!affiliation.value));

const fallback = computed(() => {
  if (affiliation.value) return null;
  if (latestStatus.value === 'PENDING') return { label: t('devRepAccount.switcher.pending'), icon: 'clock' };
  if (latestStatus.value === 'SUSPENDED') return { label: t('devRepAccount.switcher.suspended'), icon: 'shield' };
  return { label: t('devRepAccount.switcher.apply'), icon: 'building-05' };
});

const itemClass = computed(() => {
  if (props.variant === 'drawer') return 'flex min-h-12 w-full items-center gap-2 px-3 py-2.5 !text-white';
  if (props.variant === 'sheet')
    return 'flex min-h-12 items-center gap-3 px-5 py-2 text-[14px] font-medium text-black-80 hover:bg-black-03';
  return 'flex min-h-10 items-center gap-2 px-4 py-2 text-[14px] text-black-80 transition-colors hover:bg-black-03';
});
const activeClass = computed(() =>
  props.variant === 'drawer' ? 'rounded-xl bg-black-70' : 'bg-black-03 font-semibold text-black-100',
);
const headingClass = computed(() =>
  props.variant === 'drawer'
    ? 'px-3 pt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-black-60'
    : props.variant === 'sheet'
      ? 'px-5 pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-black-60'
      : 'px-4 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-black-60',
);
</script>

<template>
  <div
    v-if="visible"
    class="flex w-full flex-col"
    role="group"
    :aria-label="t('devRepAccount.switcher.title')"
  >
    <p :class="headingClass">{{ t('devRepAccount.switcher.title') }}</p>
    <template v-if="affiliation">
      <NuxtLink
        to="/developer"
        :class="[itemClass, inWorkspace ? activeClass : '']"
        :aria-current="inWorkspace ? 'page' : undefined"
        role="menuitem"
        @click="emit('navigate')"
      >
        <span aria-hidden="true">{{ PROFILE_ICONS.developer }}</span>
        <span class="min-w-0 leading-snug">{{
          t('devRepAccount.switcher.developer', { name: affiliation.developerName })
        }}</span>
      </NuxtLink>
      <NuxtLink
        to="/dashboard"
        :class="[itemClass, !inWorkspace ? activeClass : '']"
        role="menuitem"
        @click="emit('navigate')"
      >
        <span aria-hidden="true">{{ PROFILE_ICONS.personal }}</span>
        <span class="min-w-0 leading-snug">{{ t('devRepAccount.switcher.personal') }}</span>
      </NuxtLink>
    </template>
    <NuxtLink
      v-else-if="fallback"
      to="/developer/apply"
      :class="itemClass"
      role="menuitem"
      @click="emit('navigate')"
    >
      <UIIcon
        :name="fallback.icon"
        size="sm"
        class="shrink-0 text-black-100"
      />
      <span class="min-w-0 leading-snug">{{ fallback.label }}</span>
    </NuxtLink>
  </div>
</template>
